import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import { resultTierCells } from '../utils/raceFormat';
import TierPlacingFields, { emptyTierForm, resultToTierForm } from './TierPlacingFields';

const emptyForm = () => ({ race: '', race_date: '', ...emptyTierForm() });

const RaceResults = ({ birdId, readOnly = false }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Editing a result that a season chart owns: its race name/date follow the
  // chart column (renames cascade from there), so those fields lock here.
  const [editingLinked, setEditingLinked] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!birdId) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      setResults(await DatabaseService.getRaceResults(birdId));
    } catch (e) {
      console.error('Failed to load race results:', e);
    } finally {
      setLoading(false);
    }
  }, [birdId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setEditingLinked(false); setForm(emptyForm()); setError(''); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setEditingLinked(Boolean(r.chart_race_id));
    setForm({ race: r.race || '', race_date: r.race_date || '', ...resultToTierForm(r) });
    setError('');
    setDialogOpen(true);
  };

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const save = async () => {
    if (!form.race.trim()) { setError('Please enter a race name.'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingId) await DatabaseService.updateRaceResult(editingId, form);
      else await DatabaseService.addRaceResult(birdId, form);
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.isLicenseError ? e.message : 'Could not save the result. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this race result?')) return;
    try {
      await DatabaseService.deleteRaceResult(id);
      await load();
    } catch (e) {
      console.error('Failed to delete race result:', e);
    }
  };

  // The three tiers of one result, rendered as labelled chips. Each chip shows
  // its coefficient too when the field size was recorded (no birdage, no coeff).
  const resultChips = (r) => {
    const chips = resultTierCells(r);
    if (chips.length === 0) return <Typography variant="body2" color="text.secondary">No placings recorded</Typography>;
    return (
      <Box display="flex" flexWrap="wrap" gap={0.75}>
        {chips.map((c, i) => {
          const placing = c.label ? `${c.label}: ${c.text}` : c.text;
          return (
            <Chip
              key={i}
              size="small"
              label={c.coeff ? `${placing} · coeff ${c.coeff}` : placing}
              sx={{ fontWeight: 500 }}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <TrophyIcon fontSize="small" color="primary" />
          <Typography variant="h6" component="h2">Race Results</Typography>
        </Box>
        {!readOnly && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd}>
            Add result
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
      ) : results.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No race results yet{readOnly ? '.' : ' — add this bird’s placings to build its racing record.'}
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {results.map((r) => (
            <Card key={r.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box minWidth={0}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                      {r.race || 'Unnamed race'}
                    </Typography>
                    {r.race_date && (
                      <Typography variant="caption" color="text.secondary">{r.race_date}</Typography>
                    )}
                  </Box>
                  {!readOnly && (
                    <Box display="flex" flexShrink={0}>
                      <IconButton size="small" onClick={() => openEdit(r)} aria-label="Edit result">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => remove(r.id)} aria-label="Delete result">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Box mt={1}>{resultChips(r)}</Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit race result' : 'Add race result'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={7}>
              <TextField
                label="Race"
                value={form.race}
                onChange={setField('race')}
                fullWidth
                required
                autoFocus={!editingLinked}
                disabled={editingLinked}
                placeholder="e.g. Kimberley 400km"
                inputProps={{ maxLength: 120 }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Date (optional)"
                value={form.race_date}
                onChange={setField('race_date')}
                fullWidth
                disabled={editingLinked}
                placeholder="e.g. 2024-08-15"
                inputProps={{ maxLength: 120 }}
              />
            </Grid>
          </Grid>
          {editingLinked && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This result comes from a season chart — its race name and date follow the chart's
              race column and are edited there.
            </Typography>
          )}

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Placings — fill in the levels that apply
            </Typography>
          </Divider>

          <Box display="flex" flexDirection="column" gap={2}>
            <TierPlacingFields form={form} onField={setField} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            The level names default to Club, Fed and Combine — rename them to match your organisation.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add result')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RaceResults;
